import {computed, ref} from 'vue'
import {defineStore} from 'pinia'

export const useTapStore = defineStore('tap', () => {
  const tapApi = useTapApi();

  const tapList = ref([]);
  const isInitialized = ref(false)
  // `isInitialized` means that the list has been loaded at least once;
  // it does not guarantee that the cached list is still current.
  const isTapListDirty = ref(false)
  let firstTapAlias = null
  let tapListRequest = null
  let tapListRevision = 0
  let tapListSession = 0
  let imageRevision = 0
  const imageMutations = new WeakMap()
  const currentTap = ref(null)
  const currentTapLang = ref(null)
  const languageSettings = ref([])
  const view = ref(false)
  const pendingVisibility = ref(new Set())
  const usedColors = computed(() => [...new Set(
    (Array.isArray(currentTapLang.value?.usedColors) ? currentTapLang.value.usedColors : [])
      .filter(color => typeof color === 'string' && color.trim()).map(color => color.trim())
  )])

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  }

  const selectedDate = ref(formatDate(new Date()))

  const addClick = async (projectId, uniqueId, lang) => {
    return tapApi.addClick(projectId, uniqueId, lang)
  }

  const moveTapToTop = (alias) => {
    if (!alias) return
    firstTapAlias = alias
    const index = tapList.value.findIndex(tap => tap.alias === alias)
    if (index > 0) tapList.value.unshift(...tapList.value.splice(index, 1))
  }

  const getTapList = async ({ preserveCurrentTap = false } = {}) => {
    if (!preserveCurrentTap) {
      currentTap.value = null
      currentTapLang.value = null
    }
    if (tapListRequest) return tapListRequest

    const revision = tapListRevision
    const session = tapListSession
    const request = (async () => {
      try {
        const tabsInfo = await tapApi.getTaps()
        if (session !== tapListSession) return false

        tapList.value = tabsInfo.content
        moveTapToTop(firstTapAlias)
        // Changes made while the request was running still need a refresh.
        isTapListDirty.value = revision !== tapListRevision
        return true
      } catch (e) {
        if (session === tapListSession) {
          if (!preserveCurrentTap) tapList.value = []
          isTapListDirty.value = true
        }
        return false
      } finally {
        if (session === tapListSession) isInitialized.value = true
      }
    })()
    tapListRequest = request

    try {
      return await request
    } finally {
      if (tapListRequest === request) tapListRequest = null
    }
  }

  const resetTapState = () => {
    tapListSession++
    imageRevision++
    tapListRevision++
    tapListRequest = null
    tapList.value = []
    isInitialized.value = false
    isTapListDirty.value = false
    firstTapAlias = null
    currentTap.value = null
    currentTapLang.value = null
    languageSettings.value = []
    pendingVisibility.value = new Set()
    view.value = false
    selectedDate.value = formatDate(new Date())
    applyDate.value = false
  }

  const markTapListDirty = () => {
    tapListRevision++
    isTapListDirty.value = true
  }

  const checkAlias = async (name) => {
    return tapApi.checkAlias(name)
  }

  const createTap = async (name, lang) => {
    markTapListDirty()
    const tap = await tapApi.createTap(name, lang)
    moveTapToTop(name)
    return tap
  }

  const copyTap = async (name, id) => {
    markTapListDirty()
    return tapApi.copyTap(name, id)
  }

  const changeTap = async (name, id) => {
    markTapListDirty()
    if(currentTap.value) {
      currentTap.value.alias = name;
    }
    return tapApi.changeTap(name, id)
  }

  const deleteTap = async (id) => {
    markTapListDirty()
    currentTap.value = null
    currentTapLang.value = null
    languageSettings.value = []
    return tapApi.deleteTap(id)
  }

  const changeSearchVisibility = async (projectId, isSearchable) => {
    const response = await tapApi.changeSearchVisibility(projectId, isSearchable)
    const tap = tapList.value.find(item => String(item.id) === String(projectId))

    if(tap) {
      tap.searchable = isSearchable
    }

    if(currentTap.value && String(currentTap.value.id) === String(projectId)) {
      currentTap.value.searchable = isSearchable
    }

    return response
  }

  const toggleLang = async (lang) => {
    markTapListDirty()
    await tapApi.toggleLanguage(currentTap.value.projectVersion.id, lang)
    languageSettings.value.forEach(l => {
      if(l.lang === lang) l.active = true
    })
    await loadFullTap(currentTap.value.alias, lang)
  }

  const resetLang = async (lang) => {
    markTapListDirty()
    await tapApi.resetLanguage(currentTap.value.projectVersion.id, lang)
    loadFullTap(currentTap.value.alias, languageSettings.value.find(item => item.active).lang)
  }

  const loadFullTap = async (alias, lang) => {
    if(currentTap.value && currentTap.value.alias === alias &&
        ((currentTapLang.value?.lang === lang?.toUpperCase() ||
            (!lang && currentTap.value?.projectVersion.defaultLang === currentTapLang.value?.lang)) &&
            !currentTapLang.value?.edited)) return

    if((!currentTap.value || currentTap.value.alias !== alias) && lang) {
      languageSettings.value = await tapApi.getLangSettings(alias)
    }

    if(lang && languageSettings.value.length) {
      const currentLang = languageSettings.value.find(item => item.lang === lang?.toUpperCase());
      if(currentLang && !currentLang.active && currentLang.edited) {
        if(!currentTap.value) {
          currentTap.value = await tapApi.tapFull(alias)
        }
        currentTapLang.value = {lang: lang?.toUpperCase(), edited: true}
        return
      }
    }

    try {
      currentTap.value = await tapApi.tapFull(alias, lang)
      currentTapLang.value = currentTap.value.projectVersion.projectVersionLanguage
      languageSettings.value = currentTap.value.projectVersion.languageSettings
    } catch (e) {
      currentTap.value = null
      currentTapLang.value = null

      // SSR-совместимый редирект на 404
      if (process.client) {
        window.location.href = '/404'
      } else {
        throw createError({ statusCode: 404, statusMessage: 'Tap not found' })
      }
    }

    if(currentTap.value) {
      await loadTapImage(alias, lang?.toUpperCase())
    }
  }

  const loadPublicFullTap = async (alias, lang) => {
    const response = await tapApi.tapPublicFull(alias, lang)

    currentTap.value = response
    currentTapLang.value = response.projectVersion?.projectVersionLanguage

    if (currentTap.value?.pathToImage) {
      currentTap.value.image = `/proxy/tap-api.tap/api/v1/image/by-path?path=${currentTap.value.pathToImage}`
    }
  }

  const markProjectOpened = async (projectId) => {
    const session = tapListSession
    const tap = tapList.value.find(item => String(item.id) === String(projectId))
    if (tap) moveTapToTop(tap.alias)

    const response = await tapApi.markProjectOpened(projectId)
    if (session !== tapListSession) return response

    markTapListDirty()
    return response
  }

  const loadTapImage = async (alias, lang) => {
    const tap = currentTap.value
    const language = currentTapLang.value
    const languageCode = language?.lang
    const revision = imageRevision
    const session = tapListSession
    if (!tap || tap.alias !== alias || imageMutations.has(tap)) return
    const isCurrent = () => session === tapListSession && revision === imageRevision
      && currentTap.value === tap && currentTapLang.value === language && language?.lang === languageCode
    try {
      const resp = await tapApi.tapImage(alias, lang)
      if (isCurrent()) {
        tap.image = typeof resp === 'string' && resp.trim()
          ? `/proxy/tap-api.tap/api/v1/image/by-path?path=${encodeURIComponent(resp)}` : null
      }
    } catch (e) {
      if (isCurrent()) tap.image = null
    }
  }

  const setImage = async (image) => {
    const tap = currentTap.value
    const language = currentTapLang.value
    const projectId = tap?.id ?? tap?.projectId
    const lang = language?.lang
    if (!tap || projectId == null || !lang) throw new Error('Page is not ready for avatar upload')
    const revision = ++imageRevision
    const previousImage = tap.image
    const session = tapListSession
    imageMutations.set(tap, revision)
    markTapListDirty()
    tap.image = image
    tap.imageLoading = true
    try {
      if (image) await tapApi.uploadTapImage(image, projectId, lang)
      else await tapApi.deleteTapImage(projectId, lang)
      if (session === tapListSession && imageMutations.get(tap) === revision
        && currentTap.value === tap && currentTapLang.value === language && language.lang === lang) {
        language.infoChanged = true
      }
    } catch (error) {
      if (session === tapListSession && imageMutations.get(tap) === revision) tap.image = previousImage
      throw error
    } finally {
      if (imageMutations.get(tap) === revision) {
        tap.imageLoading = false
        imageMutations.delete(tap)
      }
    }
  }

  const setTapBackground = async (color) => {
    markTapListDirty()
    currentTapLang.value.backgroundColor = color
    await tapApi.updateBackground(currentTap.value.projectVersion.id, color, currentTapLang.value.lang)
  }

  const setTapSocials = async (socials, view) => {
    markTapListDirty()
    currentTapLang.value.socials = socials
    currentTapLang.value.socialView = view
    await tapApi.updateSocials(currentTap.value.projectVersion.id, socials, view, currentTapLang.value.lang)
  }

  const setTap = (tapInfo) => {
    currentTap.value = tapInfo
    if(!tapInfo) {
      currentTapLang.value = null
    }
  }

  const setTextInfo = async (title, description) => {
    markTapListDirty()
    currentTapLang.value.name = title
    currentTapLang.value.description = description
    await tapApi.updateNameDescription(currentTap.value.projectVersion.id, title, description, currentTapLang.value.lang)
  }

  const publishTap = async (publish = true) => {
    markTapListDirty()
    await tapApi.publishTap(publish ? currentTap.value.projectVersion.id : currentTap.value.id, publish, currentTapLang.value.lang)
  }

  const getBlockById = (id) => currentTapLang.value.blocks.find(b => b.id === id)

  const visibilityKey = (uniqueId) => `${currentTap.value?.projectVersion?.id}:${currentTapLang.value?.lang}:${uniqueId}`
  const isVisibilityPending = (uniqueId) => pendingVisibility.value.has(visibilityKey(uniqueId))
  const setComponentHidden = async (uniqueId, hidden) => {
    const versionId = currentTap.value?.projectVersion?.id
    const language = currentTapLang.value
    const block = language?.blocks?.find(item => item.blockItems.some(component => component.uniqueId === uniqueId))
    if (!versionId || !block) throw new Error('Missing component context')
    const key = visibilityKey(uniqueId)
    if (pendingVisibility.value.has(key)) return
    const session = tapListSession
    const component = block.blockItems.find(item => item.uniqueId === uniqueId)
    const previous = component.hidden
    pendingVisibility.value.add(key)
    component.hidden = hidden
    try {
      await tapApi.editComponent(versionId, {uniqueId, hidden}, language.lang)
      if (session !== tapListSession) return
      const saved = block.blockItems.find(item => item.uniqueId === uniqueId)
      if (saved) { saved.hidden = hidden; saved.changed = true }
      block.changed = true
      language.changed = true
      markTapListDirty()
    } catch (error) {
      if (session === tapListSession) {
        const saved = block.blockItems.find(item => item.uniqueId === uniqueId)
        if (saved) saved.hidden = previous
      }
      throw error
    } finally { if (session === tapListSession) pendingVisibility.value.delete(key) }
  }

  const refreshUsedColors = async () => {
    const tap = currentTap.value
    const language = currentTapLang.value
    const lang = language?.lang
    const versionId = tap?.projectVersion?.id
    const session = tapListSession
    if (!tap?.alias || !versionId || !lang) return false
    try {
      const response = await tapApi.tapVersionFull(versionId, lang)
      const colors = response?.projectVersion?.projectVersionLanguage?.usedColors
      if (session !== tapListSession || currentTap.value !== tap || currentTapLang.value !== language
        || language.lang !== lang || tap.projectVersion.id !== versionId
        || response?.projectVersion?.id !== versionId || !Array.isArray(colors)) return false
      language.usedColors = colors
      return true
    } catch { return false }
  }

  /*
  * Сохранение/добавление компонента в блок
  *
  * blockId - уникальный идентификатор блока (нет, если это первый компонент в блоке)
  * componentIndex - позиция вставки компонента (нет, если это первый компонент в блоке)
  * component - компонент с настройками
  * componentUniqueId - уникальный идентификатор компонента (есть если сохранение)
  * */
  const saveComponent = async (blockId, componentIndex, component, componentUniqueId) => {
    markTapListDirty()
    let block;
    const isNewBlock = blockId === undefined || blockId === null
    component['changed'] = true;
    if (isNewBlock) {
      block = {
        id: Math.floor(Math.random() * (10000 - 400 + 1)) + 400,
        orderIndex: currentTapLang.value.blocks.length + 1,
        changed: true,
        blockItems: []
      };
    } else {
      block = getBlockById(blockId)
    }

    if ((componentIndex === undefined || componentIndex === null) && !componentUniqueId) {
      //Добавление
      const data = await saveCreateComponent(component, block)
      block.uniqueId = data.uniqueId
      component.uniqueId = data.blockItem.uniqueId
      component.id = data.blockItem.id
      block.blockItems.push(component);
    } else if (componentUniqueId) {
      //Сохранение
      const index = block.blockItems.findIndex(c => c.uniqueId === componentUniqueId)
      await saveEditComponent({...component, hidden: block.blockItems[index].hidden}, componentUniqueId)
      block.blockItems[index] = {
        ...block.blockItems[index],
        ...component
      }
    } else {
      //PUT
      //Вставить перед/после
      const data = await saveCreateComponent(component, block, componentIndex)
      block.uniqueId = data.uniqueId
      component.uniqueId = data.blockItem.uniqueId
      component.id = data.blockItem.id
      block.blockItems.splice(componentIndex, 0, component);
    }
    if (isNewBlock) currentTapLang.value.blocks.push(block)
    block.changed = true
    block.blockItems = block.blockItems.map((c, i) => ({
      ...c,
      orderIndex: i + 1,
      changed: c.changed || c.orderIndex !== (i + 1)
    }));

    currentTapLang.value.blocks = currentTapLang.value.blocks.map((b, i) => ({
      ...b,
      orderIndex: i + 1,
      changed: b.changed || b.orderIndex !== (i + 1)
    }));

    currentTapLang.value.changed = true;
    if (component.type === 'tariffs') await refreshUsedColors()
  }

  const saveCreateComponent = async (cmp, block, aboveOrderIndex) => {
    let data = {
      blockUniqueId: block?.uniqueId,
      aboveOrderIndex: aboveOrderIndex,
      type: cmp.type,
      data: cmp.data
    }
    return await tapApi.addComponent(currentTap.value.projectVersion.id, data, currentTapLang.value.lang)
  }

  const saveEditComponent = async (cmp, componentUniqueId) => {
    let data = {
      uniqueId: componentUniqueId,
      type: cmp.type,
      data: cmp.data,
      ...(typeof cmp.hidden === 'boolean' && {hidden: cmp.hidden})
    }
    return await tapApi.editComponent(currentTap.value.projectVersion.id, data, currentTapLang.value.lang)
  }

  const resetChangeState = () => {
    if (!currentTapLang.value) return
    currentTapLang.value.changed = false
    currentTapLang.value.infoChanged = false
    currentTapLang.value?.blocks?.forEach(b => {
      b.changed = false
      b.blockItems.forEach(c => c.changed = false)
    })
  }

  const filled = computed(() => {
    return !!currentTapLang.value?.name ||
        !!currentTapLang.value?.description ||
        currentTapLang.value?.socials?.length > 0 ||
        !!currentTap.value?.image ||
        currentTapLang.value?.backgroundColor != '#FFFFFF'
  })

  const analytic = (uniqueId, customUniqueId) => {
    try {
      tapApi.analytics(currentTap.value.id, uniqueId, currentTapLang.value.lang, customUniqueId)
    } catch (ignore) {}
  }

  const getAnalyticsPdf = async (startDate = '', endDate = '') => {
    try {
      return await tapApi.analyticsPdf(
          currentTap.value.alias,
          currentTapLang.value.lang,
          startDate,
          endDate
      )
    } catch (e) {
      console.error(e)
    }
  }

  const getClicks = async (startDate = '', endDate = '') => {
    try {
      return await tapApi.clicks(
          currentTap.value.alias,
          currentTapLang.value.lang,
          startDate,
          endDate
      )
    } catch (ignore) {}
  }

  const getViews = async (startDate = '', endDate = '') => {
    try {
       return await tapApi.views(
           currentTap.value.alias,
           currentTapLang.value.lang,
           startDate,
           endDate
       )
    } catch (ignore) {}
  }

  const applyDate = ref(false)

  function applySelectedDate() {
    applyDate.value = true
  }

  const setDefaultLang = (lang) => {
    markTapListDirty()
    tapApi.setDefaultLang(currentTap.value.projectVersion.id, lang)
    languageSettings.value = languageSettings.value.map(item => {
      if (item.lang === lang.toUpperCase()) {
        return { ...item, active: true, orderIndex: 0 };
      }
      return {
        ...item,
        active: false,
        orderIndex: item.orderIndex >= 0 ? item.orderIndex + 1 : 1
      };
    });
  }

  const setLanguages = async (data, router, basePath) => {
    markTapListDirty()
    const currentLang = data.find(item => item.lang === currentTapLang.value.lang);
    if (currentLang && !currentLang.active) {
      currentLang.edited = currentLang.edited || currentTap.value.changed;
    }
    languageSettings.value = data
    await tapApi.setLanguages(currentTap.value.projectVersion.id, data)

    if (currentLang && !currentLang.active && !currentLang.edited) {
      const activeLang = data.find(item => item.active).lang.toLowerCase();
      // Используем navigateTo для Nuxt вместо router.push
      return navigateTo(`${basePath}/${activeLang}`)
    } else if(currentLang && !currentLang.active) {
      currentTapLang.value = {lang: currentLang.lang, edited: true}
    }
  }

  return {
    tapList,
    isInitialized,
    isTapListDirty,
    getTapList,
    moveTapToTop,
    resetTapState,
    markTapListDirty,
    setTap,
    setImage,
    setTextInfo,
    currentTap,
    currentTapLang,
    loadFullTap,
    loadPublicFullTap,
    markProjectOpened,
    saveComponent,
    setComponentHidden,
    isVisibilityPending,
    usedColors,
    refreshUsedColors,
    checkAlias,
    createTap,
    copyTap,
    changeTap,
    deleteTap,
    changeSearchVisibility,
    view,
    resetChangeState,
    setTapBackground,
    setTapSocials,
    publishTap,
    getClicks,
    getViews,
    filled,
    analytic,
    setDefaultLang,
    setLanguages,
    toggleLang,
    resetLang,
    languageSettings,
    selectedDate,
    addClick,
    getAnalyticsPdf,
    applySelectedDate,
    applyDate
  }
})
